import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { BsCheckCircle, BsBoxSeam, BsArrowRight } from "react-icons/bs";
import { SITE_BASE_URL } from "../../config/api";
import { clearCheckoutSession } from "../../utils/checkoutRecovery";

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const orderNumber = searchParams.get("order");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    clearCheckoutSession();
  }, []);

  const trackingUrl = token
    ? `${SITE_BASE_URL}/track/${token}`
    : `${SITE_BASE_URL}/track`;

  const copyTrackingLink = async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(trackingUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <>
      <Helmet>
        <title>Order Confirmed | Knots & Krafts</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-16 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-lg mx-auto bg-white rounded-2xl shadow-lg border border-amber-100 p-8 text-center"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <BsCheckCircle className="text-3xl text-green-600" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Payment Successful!
          </h1>
          <p className="text-gray-600 mb-6">
            Thank you for your order. A confirmation email has been sent with
            your tracking details.
          </p>

          {orderNumber && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-gray-500 mb-1">Order Number</p>
              <p className="text-lg font-semibold text-gray-900">{orderNumber}</p>
            </div>
          )}

          {token && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6 text-left">
              <div className="flex items-center gap-2 mb-2">
                <BsBoxSeam className="text-amber-600" />
                <p className="text-sm font-medium text-gray-700">
                  Track your order anytime
                </p>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                No login required — save this link to check status later.
              </p>
              <Link
                to={`/track/${token}`}
                className="inline-flex items-center gap-2 text-amber-700 font-medium text-sm hover:text-amber-800"
              >
                View order status <BsArrowRight />
              </Link>
              <button
                type="button"
                onClick={copyTrackingLink}
                className="block mt-3 text-xs text-gray-500 hover:text-gray-700 underline"
              >
                {copied ? "Link copied!" : "Copy tracking link"}
              </button>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {token && (
              <Link
                to={`/track/${token}`}
                className="inline-flex justify-center items-center px-6 py-3 bg-amber-500 text-white font-semibold rounded-xl hover:bg-amber-600 transition-colors"
              >
                Track Order
              </Link>
            )}
            <Link
              to="/shop"
              className="inline-flex justify-center items-center px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
            >
              Continue Shopping
            </Link>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default CheckoutSuccessPage;
